import React, { forwardRef, Ref, useEffect, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  View,
  ViewStyle,
} from 'react-native';
import {
  CodeField,
  Cursor,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';

import generateID from '../../utils/generate-id';

type CodeInputProps = {
  accessibilityLabel?: string;
  errorMessage?: string;
  id?: string;
  length?: number;
  onBlur?: TextInputProps['onBlur'];
  onFocus?: TextInputProps['onFocus'];
  onFulFill: (code: string) => void;
  showError?: boolean;
  style?: StyleProp<ViewStyle>;
};

const CodeInput = forwardRef((props: CodeInputProps, ref: Ref<TextInput>) => {
  const cellCount = props.length || 4;
  const [value, setValue] = useState('');
  const [codeFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  });

  useEffect(() => {
    if (value.length === cellCount) {
      props.onFulFill(value);
    }
  }, [value]);

  // Generate IDs for the CodeInput sub-components
  const textInputID = generateID(props.id);

  const messageID = generateID(props.id && `${props.id}-message`);

  return (
    <View style={props.style}>
      <CodeField
        {...codeFieldProps}
        ref={ref}
        accessibilityLabel={props.accessibilityLabel}
        cellCount={cellCount}
        keyboardType="phone-pad"
        onChangeText={setValue}
        textContentType="oneTimeCode"
        value={value}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        renderCell={(cell) => (
          <Text
            accessibilityLabel={`digit_${cell.index + 1}`}
            key={cell.index}
            style={[
              style.cell,
              cell.isFocused && style.cellFocused,
              props.showError && style.cellError,
            ]}
            onLayout={getCellOnLayoutHandler(cell.index)}
          >
            {cell.symbol || (cell.isFocused ? <Cursor /> : null)}
          </Text>
        )}
        textInputStyle={style.hiddenTextField}
        /** Generate HTML and Test IDs  */
        {...textInputID}
        /** Web-specific props */
        {...Platform.select({
          web: {
            // TODO: REQUIRES REACT 17 TO WORK, SO PLEASE UPGRADE
            accessibilityDescribedBy: props.showError
              ? messageID.nativeID
              : undefined,
          },
        })}
      />
      {props.showError && (
        <Text
          style={style.message}
          /** Generate HTML and Test IDs  */
          {...messageID}
        >
          {props.errorMessage}
        </Text>
      )}
    </View>
  );
});

const style = StyleSheet.create({
  cell: {
    borderColor: '#CFD4D8',
    fontSize: 32,
    fontWeight: 'bold',
    width: 56,
    height: 64,
    lineHeight: 56,
    textAlign: 'center',
    color: '#2A2E32',
    backgroundColor: '#F0F0F0',
    borderRadius: 4,
  },
  cellFocused: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 2,
    borderColor: '#404042',
  },
  cellError: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 2,
    borderColor: '#CA4444',
  },
  hiddenTextField: {
    height: 64,
  },
  message: {
    marginTop: 16,
    fontSize: 12,
    fontWeight: '500',
    color: '#CA4444',
    textAlign: 'center',
  },
});

export default CodeInput;
export { CodeInputProps };
